import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { FiInbox, FiMail, FiCheck, FiCheckCircle, FiArrowRight, FiBell } from 'react-icons/fi';
import { messagesApi } from '../../utils/api';
import './MessagesPage.css';

const MessagesPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [page, setPage] = useState(1);
  const [showNewMessageAlert, setShowNewMessageAlert] = useState(false);
  const [userClosedMessage, setUserClosedMessage] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Track window resize for mobile detection
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const { data, isLoading } = useQuery(
    ['messages', page],
    () => messagesApi.getAll({ page, limit: 20 }),
    { keepPreviousData: true }
  );

  const markAllReadMutation = useMutation(
    () => messagesApi.markAllRead(),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('messages');
      }
    }
  );

  const messages = data?.data?.messages || [];
  const pagination = data?.data?.pagination || {};
  const unreadCount = data?.data?.unreadCount || 0;

  // Auto-select first message when messages load (only on desktop, and only if user didn't close)
  useEffect(() => {
    if (messages.length > 0 && !selectedMessage && !userClosedMessage && !isMobile) {
      setSelectedMessage(messages[0]);

      // Show new message alert if there are unread messages
      if (unreadCount > 0) {
        setShowNewMessageAlert(true);
        // Hide alert after 5 seconds
        setTimeout(() => setShowNewMessageAlert(false), 5000);
      }
    }
  }, [messages, selectedMessage, unreadCount, userClosedMessage, isMobile]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'אתמול';
    } else if (diffDays < 7) {
      return date.toLocaleDateString('he-IL', { weekday: 'long' });
    } else {
      return date.toLocaleDateString('he-IL', { day: 'numeric', month: 'short' });
    }
  };

  const handleSelectMessage = (message) => {
    setSelectedMessage(message);
    setUserClosedMessage(false); // Reset flag when user selects a message
    // Mark as read when opening
    if (!message.is_read) {
      queryClient.invalidateQueries('messages');
    }
  };

  const handleCloseMessage = () => {
    // On mobile, go back to messages list. On desktop, go to dashboard
    if (isMobile && selectedMessage) {
      setSelectedMessage(null);
      setUserClosedMessage(true);
    } else {
      navigate('/dashboard');
    }
  };

  return (
    <div className="messages-page">
      {/* New Message System Alert */}
      <AnimatePresence>
        {showNewMessageAlert && (
          <motion.div
            className="new-message-system-alert"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <FiBell className="alert-icon" />
            <span>שימו לב! נכנסה הודעה חדשה</span>
            <button
              className="alert-close"
              onClick={() => setShowNewMessageAlert(false)}
            >
              ✕
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="page-header">
        <div className="header-content">
          {isMobile && !selectedMessage && (
            <button
              type="button"
              className="btn-back-to-dashboard"
              onClick={() => navigate('/dashboard')}
            >
              <FiArrowRight />
            </button>
          )}
          <h1>הודעות</h1>
          {unreadCount > 0 && (
            <span className="unread-badge">{unreadCount} לא נקראו</span>
          )}
        </div>
        {unreadCount > 0 && (
          <button
            className="btn btn-outline btn-sm"
            onClick={() => markAllReadMutation.mutate()}
            disabled={markAllReadMutation.isLoading}
          >
            <FiCheckCircle /> סמן הכל כנקרא
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="loading-state">
          <div className="spinner"></div>
        </div>
      ) : messages.length === 0 ? (
        <div className="empty-state">
          <FiInbox className="empty-icon" />
          <h3>אין הודעות</h3>
          <p>כשתקבל הודעות ממנהלי המערכת, הן יופיעו כאן</p>
        </div>
      ) : (
        <div className={`messages-container ${selectedMessage ? 'viewing-message' : ''}`}>
          {/* Messages List */}
          <div className="messages-list">
            {messages.map((message) => (
              <motion.div
                key={message.id}
                className={`message-item ${!message.is_read ? 'unread' : ''} ${selectedMessage?.id === message.id ? 'selected' : ''}`}
                onClick={() => handleSelectMessage(message)}
                whileHover={{ backgroundColor: '#f8f8fc' }}
              >
                <div className="message-indicator">
                  {!message.is_read ? (
                    <span className="unread-dot" />
                  ) : (
                    <FiCheck className="read-icon" />
                  )}
                </div>
                <div className="message-content">
                  <div className="message-header">
                    <span className="sender-name">{message.sender_name}</span>
                    <span className="message-date">{formatDate(message.created_at)}</span>
                  </div>
                  <div className="message-subject">
                    {message.subject || 'ללא נושא'}
                  </div>
                  <div className="message-preview">
                    {message.content.substring(0, 80)}...
                  </div>
                  <span className="click-to-view">לחצו כאן לצפייה בהודעה</span>
                  {message.campaign_title && (
                    <span className="campaign-tag">{message.campaign_title}</span>
                  )}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Message Detail - Always visible when there are messages */}
          <div className={`message-detail ${selectedMessage ? 'active' : ''}`}>
            {selectedMessage && (
              <>
                <div className="detail-header">
                  <button
                    type="button"
                    className="btn-back"
                    onClick={handleCloseMessage}
                  >
                    <FiArrowRight />
                  </button>
                  <h2>{selectedMessage.subject || 'ללא נושא'}</h2>
                </div>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >

                <div className="message-received-banner">
                  <FiMail className="banner-icon" />
                  <span>
                    התקבלה בתאריך {new Date(selectedMessage.created_at).toLocaleDateString('he-IL', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })} בשעה {new Date(selectedMessage.created_at).toLocaleTimeString('he-IL', {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>

                <div className="detail-meta">
                  <div className="sender-info">
                    <span>מאת: מערכת Weon</span>
                  </div>
                </div>

                {selectedMessage.campaign_title && (
                  <div className="detail-campaign">
                    קמפיין: {selectedMessage.campaign_title}
                  </div>
                )}

                <div className="detail-content">
                  {selectedMessage.content.split('\n').map((line, i) => (
                    <p key={i}>{line || <br />}</p>
                  ))}
                </div>
                </motion.div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="pagination">
          <button
            className="btn btn-outline"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
          >
            הקודם
          </button>
          <span className="page-info">
            עמוד {page} מתוך {pagination.pages}
          </span>
          <button
            className="btn btn-outline"
            disabled={page === pagination.pages}
            onClick={() => setPage(page + 1)}
          >
            הבא
          </button>
        </div>
      )}
    </div>
  );
};

export default MessagesPage;
